// =============================================================================
// ManageSecurityModal.jsx — KlassDoc Sprint 6.5 Wave 2.1
// -----------------------------------------------------------------------------
// "Gérer la sécurité" / "Manage Security" dialogue.
//
// PURPOSE
// Opens from the document preview modal footer for users with Full Access.
// Allows editing the document's ACL after it's been created. For users with
// less than Full Access, opens in READ-ONLY transparency mode showing the
// current ACL but with all controls disabled.
//
// FLOW
//   1. Parent renders <ManageSecurityModal doc={doc} myAccess={...} ... />
//   2. Modal calls GET /api/permissions/document/:id to load current ACL
//   3. User adds/removes/edits principals (if they have Full Access)
//   4. User clicks "Save" → POST /api/permissions/document/:id with new entries
//   5. Modal calls onSuccess() and closes
//
// PROPS
//   doc          : The document object with id and uploaded_by_id
//   myAccess     : Current user's access level on this document
//   onClose      : Callback when user cancels or closes
//   onSuccess    : Callback after successful save
//   api          : Shared ApiClient instance
//   currentUser  : Logged-in user object
//   lang         : "fr" or "en"
//   THEME        : Shared theme object
//
// READ-ONLY MODE
//   When myAccess !== "full" and user is not admin, the modal opens in
//   transparency mode: ACL is visible (so user knows who to ask for access)
//   but no controls are interactive. Save button is hidden.
// =============================================================================

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import * as lucide from "lucide-react";
import { PrincipalPicker, AclEntryRow } from "./security/SecurityControls";

function fmt(lang, fr, en) { return lang === "fr" ? fr : en; }

export default function ManageSecurityModal({ doc, myAccess, onClose, onSuccess, api, currentUser, lang = "fr", THEME }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [aclEntries, setAclEntries] = useState([]);
  const [adminGroupId, setAdminGroupId] = useState(null);

  // Read-only mode if user doesn't have full access (and isn't admin)
  const canEdit = myAccess === "full" || currentUser?.role === "admin";

  // Load current ACL + administrators group ID
  useEffect(() => {
    if (!doc?.id) return;
    setLoading(true);
    Promise.all([
      api.get(`/permissions/document/${doc.id}`),
      api.get("/groups"),
    ]).then(([permRes, groupsRes]) => {
      const admins = (groupsRes.data || []).find(g => g.name === "administrators");
      const adminId = admins?.id || null;
      setAdminGroupId(adminId);

      // Mark uploader and admin group as locked
      const entries = (permRes.data || []).map(e => {
        const isUploader = e.principal_type === "user" && e.principal_id === doc.uploaded_by_id;
        const isAdminGroup = e.principal_type === "group" && e.principal_id === adminId;
        return {
          principal_type: e.principal_type,
          principal_id: e.principal_id,
          principal_name: e.principal_name,
          access_level: e.access_level,
          locked: isUploader || isAdminGroup,
        };
      });
      setAclEntries(entries);
      setLoading(false);
    }).catch(e => {
      console.error("Load ACL error:", e);
      setError(fmt(lang, "Échec du chargement des permissions", "Failed to load permissions"));
      setLoading(false);
    });
  }, [doc?.id, api, lang]);

  // Escape key closes modal
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && !saving) onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  function addPrincipal(p) { setAclEntries(prev => [...prev, p]); }
  function updatePrincipalAccess(idx, level) {
    setAclEntries(prev => prev.map((e, i) => i === idx ? { ...e, access_level: level } : e));
  }
  function removePrincipal(idx) {
    setAclEntries(prev => prev.filter((_, i) => i !== idx));
  }

  const excludeIds = new Set(aclEntries.map(e => `${e.principal_type}-${e.principal_id}`));

  async function handleSave() {
    if (!canEdit) return;
    setError(null);
    setSaving(true);
    try {
      const entries = aclEntries.filter(e => e.principal_id).map(e => ({
        principal_type: e.principal_type,
        principal_id: e.principal_id,
        principal_name: e.principal_name,
        access_level: e.access_level,
      }));
      await api.post(`/permissions/document/${doc.id}`, { entries });
      onSuccess?.();
    } catch (e) {
      console.error("Save ACL error:", e);
      setError(e.message || fmt(lang, "Échec de l'enregistrement", "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  const FONT = "'DM Sans','Satoshi',-apple-system,sans-serif";
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 600, color: THEME.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.3 };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: FONT }}
      onClick={e => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
        style={{ background: THEME.surfaceRaised, border: `1px solid ${THEME.border}`, borderRadius: 16, width: "100%", maxWidth: 680, maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 32px 96px rgba(0,0,0,0.6)" }}
      >
        {/* HEADER */}
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${THEME.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 11, background: `linear-gradient(135deg, ${THEME.warning}, ${THEME.warning}CC)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 16px ${THEME.warning}40` }}>
              <lucide.Shield size={22} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: THEME.text }}>{fmt(lang, "Gérer la sécurité", "Manage Security")}</div>
              <div style={{ fontSize: 12, color: THEME.textDim, marginTop: 2 }}>
                {doc?.name}
                {!canEdit && (
                  <span style={{ marginLeft: 8, padding: "2px 8px", borderRadius: 4, background: THEME.infoDim, color: THEME.info, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>
                    {fmt(lang, "Lecture seule", "View Only")}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={() => !saving && onClose()} disabled={saving} style={{ background: "transparent", border: "none", cursor: saving ? "not-allowed" : "pointer", padding: 6, opacity: saving ? 0.4 : 1 }}>
            <lucide.X size={20} color={THEME.textMuted} />
          </button>
        </div>

        {/* CONTENT */}
        <div style={{ flex: 1, overflow: "auto", padding: "24px 28px" }}>
          {error && (
            <div style={{ padding: "12px 16px", marginBottom: 16, background: THEME.dangerDim, border: `1px solid ${THEME.danger}40`, borderRadius: 8, color: THEME.danger, fontSize: 13, display: "flex", alignItems: "center", gap: 10 }}>
              <lucide.AlertCircle size={16} />{error}
            </div>
          )}

          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 60, gap: 12 }}>
              <lucide.Loader2 size={24} color={THEME.accent} className="spin" />
              <div style={{ fontSize: 13, color: THEME.textDim }}>{fmt(lang, "Chargement des permissions...", "Loading permissions...")}</div>
            </div>
          ) : (
            <>
              {/* Info banner — different message based on canEdit */}
              <div style={{ padding: 14, marginBottom: 20, background: canEdit ? THEME.accentDim : THEME.infoDim, border: `1px solid ${(canEdit ? THEME.accent : THEME.info)}40`, borderRadius: 10, display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: (canEdit ? THEME.accent : THEME.info) + "1A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <lucide.Info size={18} color={canEdit ? THEME.accent : THEME.info} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: THEME.text }}>
                    {canEdit
                      ? fmt(lang, "Vous avez le contrôle total", "You have full control")
                      : fmt(lang, "Visualisation transparente", "Transparent view")}
                  </div>
                  <div style={{ fontSize: 12, color: THEME.textDim, marginTop: 3, lineHeight: 1.5 }}>
                    {canEdit
                      ? fmt(lang,
                          "Ajoutez ou retirez des utilisateurs et groupes. Les utilisateurs non listés n'auront aucun accès au document.",
                          "Add or remove users and groups. Users not listed below will have no access to this document.")
                      : fmt(lang,
                          "Vous voyez qui a accès à ce document. Pour modifier les permissions, contactez l'auteur ou un administrateur.",
                          "You can see who has access to this document. To modify permissions, contact the author or an administrator.")}
                  </div>
                </div>
              </div>

              {/* Add principal picker — only if canEdit */}
              {canEdit && (
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>{fmt(lang, "Ajouter des utilisateurs/groupes", "Add Users/Groups")}</label>
                  <PrincipalPicker api={api} lang={lang} THEME={THEME} onAdd={addPrincipal} excludeIds={excludeIds} />
                </div>
              )}

              {/* ACL list */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: THEME.textMuted, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>{aclEntries.length} {fmt(lang, "principaux avec permissions", "principals with permissions")}</span>
                  {!canEdit && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4, color: THEME.info }}>
                      <lucide.Eye size={11} /> {fmt(lang, "Lecture seule", "View only")}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {aclEntries.length === 0 ? (
                    <div style={{ padding: 30, textAlign: "center", color: THEME.textDim, fontSize: 13 }}>
                      {fmt(lang, "Aucune permission définie", "No permissions defined")}
                    </div>
                  ) : aclEntries.map((entry, idx) => (
                    <AclEntryRow
                      key={`${entry.principal_type}-${entry.principal_id}-${idx}`}
                      entry={entry}
                      onChangeAccess={lvl => updatePrincipalAccess(idx, lvl)}
                      onRemove={() => removePrincipal(idx)}
                      lang={lang}
                      THEME={THEME}
                      readOnly={!canEdit}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${THEME.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: THEME.surface }}>
          <div style={{ fontSize: 11, color: THEME.textDim, display: "flex", alignItems: "center", gap: 6 }}>
            <lucide.Lock size={11} />
            {fmt(lang, "L'auteur et le groupe Administrateurs sont verrouillés", "Author and Administrators group are locked")}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" onClick={onClose} disabled={saving} style={{ padding: "10px 20px", background: "transparent", color: THEME.textMuted, border: `1px solid ${THEME.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.4 : 1, fontFamily: FONT }}>
              {canEdit ? fmt(lang, "Annuler", "Cancel") : fmt(lang, "Fermer", "Close")}
            </button>
            {canEdit && (
              <button type="button" onClick={handleSave} disabled={saving || loading} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 24px", background: `linear-gradient(135deg, ${THEME.warning}, ${THEME.warning}CC)`, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: (saving || loading) ? "wait" : "pointer", boxShadow: `0 4px 16px ${THEME.warning}40`, fontFamily: FONT, opacity: (saving || loading) ? 0.7 : 1 }}>
                {saving ? <lucide.Loader2 size={15} className="spin" /> : <lucide.Save size={15} />}
                {saving ? fmt(lang, "Enregistrement...", "Saving...") : fmt(lang, "Enregistrer", "Save")}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
